<!--
设备管理页面组件
被主导航和仪表盘调用
权限点：device.manage
简要说明：用于管理和监控所有设备状态，所有操作按钮需加权限判断
-->
<template>
  <div class="device-management">
    <!-- 音频元素用于播放报警音效 -->
    <audio
      id="deviceManagementAlertSound"
      src="/sound/alarm.mp3"
      preload="auto"
      style="display: none"
    ></audio>
    <!-- 搜索筛选区域 -->
    <div class="search-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input
            v-model="searchForm.device_id"
            placeholder="设备编号"
            clearable
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="6">
          <el-input
            v-model="searchForm.deviceName"
            placeholder="设备名称"
            clearable
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="searchForm.status"
            placeholder="设备连接状态"
            clearable
          >
            <el-option label="全部" value="" />
            <el-option label="在线" value="online" />
            <el-option label="离线" value="offline" />
          </el-select>
        </el-col>
        <el-col :span="4" v-if="isSuperAdmin">
          <el-select
            v-model="searchForm.license_status"
            placeholder="激活状态"
            clearable
          >
            <el-option label="全部" value="" />
            <el-option label="已激活" value="active" />
            <el-option label="未激活" value="inactive" />
            <el-option label="已过期" value="expired" />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-input v-model="searchForm.imsi" placeholder="IMSI" clearable>
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </el-col>
        <el-col :span="4">
          <div class="button-group">
            <el-button
              type="primary"
              @click="handleSearch"
              v-if="hasPermission('device.manage')"
            >
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
            <el-button
              @click="resetSearch"
              v-if="hasPermission('device.manage')"
            >
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧：设备列表 -->
      <div class="left-content">
        <el-card class="list-card">
          <template #header>
            <div class="card-header">
              <span>设备列表</span>
              <div class="header-actions">
                <el-button
                  type="primary"
                  @click="addDevice"
                  v-if="hasPermission('device.create')"
                >
                  <el-icon><Plus /></el-icon>
                  注册新设备
                </el-button>
              </div>
            </div>
          </template>

          <el-table
            :data="devices"
            style="width: 100%"
            table-layout="auto"
            v-loading="loading"
          >
            <el-table-column type="selection" width="55" />
            <el-table-column prop="device_id" label="设备编号" width="150" />
            <el-table-column
              prop="device_name"
              label="设备名称"
              min-width="180"
            />
            <el-table-column prop="status" label="连接状态" width="140">
              <template #default="scope">
                <el-space alignment="center">
                  <el-tag :type="getStatusType(scope.row.status)">
                    {{ getStatusText(scope.row.status) }}
                  </el-tag>
                  <template v-if="scope.row.status === 'online'">
                    <el-tooltip
                      :content="getNetworkTypeTooltip(scope.row)"
                      placement="top"
                    >
                      <img
                        v-if="isWifi(scope.row)"
                        :src="wifiIcon"
                        class="net-icon"
                        alt="WiFi"
                      />
                      <img
                        v-else-if="isCellular(scope.row)"
                        :src="cellularIcon"
                        class="net-icon"
                        alt="4G"
                      />
                    </el-tooltip>
                  </template>
                </el-space>
              </template>
            </el-table-column>
            <el-table-column
              prop="license_status"
              label="License状态"
              width="120"
              v-if="isSuperAdmin"
            >
              <template #default="scope">
                <el-tag :type="getLicenseStatusType(scope.row.license_status)">
                  {{ getLicenseStatusText(scope.row.license_status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="last_seen_at"
              label="最后在线时间"
              min-width="160"
            >
              <template #default="scope">
                {{ formatDateTime(scope.row.last_seen_at) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="firmware_version"
              label="固件版本"
              width="120"
            >
              <template #default="scope">
                <el-tag
                  :type="scope.row.firmware_version ? 'info' : 'warning'"
                  size="small"
                >
                  {{ scope.row.firmware_version || "未知" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              label="操作"
              width="260"
              fixed="right"
              align="center"
            >
              <template #default="scope">
                <div
                  class="action-buttons"
                  style="
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                  "
                >
                  <el-button
                    type="primary"
                    size="small"
                    @click="viewDevice(scope.row)"
                  >
                    详情
                  </el-button>
                  <el-button
                    type="success"
                    size="small"
                    @click="viewCallRecords(scope.row)"
                  >
                    通话明细
                  </el-button>
                  <el-button
                    type="info"
                    size="small"
                    @click="initiateCall(scope.row)"
                    v-if="hasPermission('device.call')"
                    :disabled="scope.row.status !== 'online'"
                  >
                    <el-icon><Phone /></el-icon>
                    呼叫
                  </el-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="last_restart"
              label="最近重启"
              min-width="180"
            >
              <template #default="scope">
                <el-tooltip
                  v-if="scope.row.__lastRestartDisplay"
                  :content="scope.row.__lastRestartDisplay"
                  placement="top"
                >
                  <span>{{ scope.row.__lastRestartDisplay }}</span>
                </el-tooltip>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <el-pagination
              v-model:current-page="pagination.page"
              v-model:page-size="pagination.pageSize"
              :page-sizes="[10, 20, 50, 100]"
              :total="pagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            />
          </div>
        </el-card>
      </div>

      <!-- 右侧：统计面板 -->
      <div class="right-content">
        <!-- 设备状态统计 -->
        <el-card class="stats-card" header="设备状态统计">
          <div class="stats-grid">
            <div class="stat-item online">
              <div class="stat-icon">
                <el-icon><Monitor /></el-icon>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ deviceStats.online }}</div>
                <div class="stat-label">在线</div>
              </div>
            </div>

            <div class="stat-item offline">
              <div class="stat-icon">
                <el-icon><Monitor /></el-icon>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ deviceStats.offline }}</div>
                <div class="stat-label">离线</div>
              </div>
            </div>

            <div class="stat-item error">
              <div class="stat-icon">
                <el-icon><Warning /></el-icon>
              </div>
              <div class="stat-content">
                <div class="stat-number">
                  {{ deviceStats.license_stats?.expired || 0 }}
                </div>
                <div class="stat-label">License过期</div>
              </div>
            </div>

            <div class="stat-item total">
              <div class="stat-icon">
                <el-icon><DataBoard /></el-icon>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ deviceStats.total }}</div>
                <div class="stat-label">设备总数</div>
              </div>
            </div>
          </div>
        </el-card>

        <!-- 区域分布 (暂时注释，需要后端提供数据) -->
        <!--
        <el-card class="area-card" header="区域分布">
          <div class="area-list">
            <div class="area-item" v-for="area in areaStats" :key="area.name">
              <div class="area-info">
                <span class="area-name">{{ area.name }}</span>
                <span class="area-count">{{ area.count }}台</span>
              </div>
              <div class="area-progress">
                <el-progress
                  :percentage="(area.count / deviceStats.total) * 100"
                />
              </div>
            </div>
          </div>
        </el-card>
        -->

        <!-- 固件版本分布 (暂时注释) -->
        <!--
        <el-card class="firmware-card" header="固件版本分布">
          <div class="firmware-list">
            <div
              class="firmware-item"
              v-for="fw in firmwareStats"
              :key="fw.version"
            >
              <span class="firmware-version">
                {{ fw.version }}
                <el-tag v-if="fw.isLatest" size="small" type="success">最新</el-tag>
              </span>
              <span class="firmware-count">{{ fw.count }}台</span>
            </div>
          </div>
        </el-card>
        -->
      </div>
    </div>

    <!-- 新增/编辑设备弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      @close="closeDialog"
    >
      <el-form
        :model="deviceForm"
        :rules="deviceFormRules"
        ref="deviceFormRef"
        label-width="100px"
      >
        <el-form-item label="设备编号" prop="device_id">
          <el-input
            v-model="deviceForm.device_id"
            :disabled="isEditMode"
            placeholder="例如：f1-a0-11-2d-5c-66"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备名称" prop="device_name">
          <el-input
            v-model="deviceForm.device_name"
            placeholder="例如：一楼大厅紧急呼叫器"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备型号" prop="device_model">
          <el-input
            v-model="deviceForm.device_model"
            placeholder="例如：EBA-2024"
          ></el-input>
        </el-form-item>
        <el-form-item label="硬件版本" prop="hardware_version">
          <el-input
            v-model="deviceForm.hardware_version"
            placeholder="例如：1.0.2"
          ></el-input>
        </el-form-item>
        <el-form-item label="固件版本" prop="firmware_version">
          <el-input
            v-model="deviceForm.firmware_version"
            placeholder="例如：2.1.5"
          ></el-input>
        </el-form-item>
        <el-form-item label="连接状态" prop="status" v-if="isEditMode">
          <el-select v-model="deviceForm.status" placeholder="设备连接状态">
            <el-option label="在线" value="online" />
            <el-option label="离线" value="offline" />
          </el-select>
        </el-form-item>
        <el-form-item
          label="License状态"
          prop="license_status"
          v-if="isEditMode"
        >
          <el-select
            v-model="deviceForm.license_status"
            placeholder="License状态"
          >
            <el-option label="已激活" value="active" />
            <el-option label="未激活" value="inactive" />
            <el-option label="已过期" value="expired" />
          </el-select>
        </el-form-item>
        <el-form-item label="License密钥" prop="license_key" v-if="isEditMode">
          <el-input
            v-model="deviceForm.license_key"
            placeholder="请输入License密钥"
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="handleSave">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, onUnmounted, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox, FormInstance, FormRules } from "element-plus";
import {
  Search,
  Refresh,
  Plus,
  Monitor,
  Warning,
  DataBoard,
  Phone,
} from "@element-plus/icons-vue";
import wifiIcon from "@/assets/images/WIFI.svg";
import cellularIcon from "@/assets/images/Cellular 4G-1.svg";
import { useStore } from "vuex";
import apiClient from "../services/apiClient";
import config from "@/config";
import { logger } from "../utils/logger";
import { DataService } from "../services/dataService";
import { formatDateTime } from "../utils/dateTimeUtils";
import jsCookie from "js-cookie";
import {
  useCallDialog,
  getDeviceDisplayName,
} from "@/composables/useCallDialog";

// 设备类型定义
interface Device {
  device_id: string;
  device_name: string;
  status?: string;
  license_status?: string;
  network_type?: string;
  firmware_version?: string;
  last_seen_at?: string | Date | null;
  __lastRestartDisplay?: string | null;
  last_restart_reason?: string | null;
  last_restart_time?: string | number | Date | null;
}

type DevicesStats = {
  online?: number;
  offline?: number;
  total?: number;
  license_stats?: { active?: number; inactive?: number; expired?: number };
};

type DevicesListResponse = {
  code: number;
  data: { total: number; items: Device[]; stats?: DevicesStats };
  message?: string;
};

const store = useStore();
const router = useRouter();
let unsubscribe: null | (() => void) = null;
let unsubscribeStatus: null | (() => void) = null;
// 中文注释：会话级持久化键，用于保存筛选与分页参数
const STORAGE_KEY = "device_mgmt_filters_v1";
const hasPermission = (perm: string) => store.getters.hasPermission(perm);
// 检查是否为超级管理员
const isSuperAdmin = computed(() => {
  const userRole = store.state.user?.role;
  return userRole === "super_admin";
});

// 响应式状态
const loading = ref(false);
const devices = ref<Device[]>([]);
const searchForm = reactive({
  device_id: "",
  deviceName: "",
  status: "",
  license_status: "", // 新增：License状态筛选
  imsi: "", // 新增：IMSI筛选
});
const pagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0,
});

// 新增/编辑弹窗的状态
const dialogVisible = ref(false);
const dialogTitle = ref("");
const isEditMode = ref(false);
const deviceFormRef = ref<FormInstance>();
const deviceForm = reactive({
  id: null,
  device_id: "",
  device_name: "",
  device_model: "",
  hardware_version: "",
  firmware_version: "",
  status: "offline",
  license_status: "inactive",
  license_key: "",
});
const deviceFormRules = reactive<FormRules>({
  device_id: [{ required: true, message: "设备编号不能为空", trigger: "blur" }],
  device_name: [
    { required: true, message: "设备名称不能为空", trigger: "blur" },
  ],
});

// 为统计数据创建响应式状态
const deviceStats = reactive({
  online: 0,
  offline: 0,
  total: 0,
  license_stats: {
    active: 0,
    inactive: 0,
    expired: 0,
  },
});

// 获取设备列表
const fetchDevices = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
      device_id: searchForm.device_id || undefined,
      device_name: searchForm.deviceName || undefined,
      status: searchForm.status || undefined,
      license_status: searchForm.license_status || undefined, // 新增：License状态筛选
      imsi: searchForm.imsi || undefined, // 新增：IMSI筛选
    };

    // 使用数据服务层
    const response = await DataService.getDevices(params);
    const raw = (response as { data?: unknown })?.data ?? response;
    const responseData = raw as DevicesListResponse;

    if (responseData.code === 0) {
      devices.value = responseData.data.items;
      pagination.total = responseData.data.total;
      // 更新统计数据
      if (responseData.data.stats) {
        Object.assign(deviceStats, responseData.data.stats);
      }
      // 处理设备列表中的重启原因信息（直接从API响应中获取，无需额外请求）
      (devices.value || []).forEach((d: Device) => {
        if (d.last_restart_reason && d.last_restart_time) {
          // 直接使用formatDateTime处理，不要调用toISOString()，因为后端返回的已经是本地时间
          d.__lastRestartDisplay = `${d.last_restart_reason} @ ${formatDateTime(
            String(d.last_restart_time)
          )}`;
        } else if (d.last_restart_reason) {
          d.__lastRestartDisplay = d.last_restart_reason;
        } else {
          d.__lastRestartDisplay = null;
        }
      });

      // 自动获取设备名称
      await fetchDeviceNames();
    } else {
      ElMessage.error(responseData.message || "获取设备列表失败");
    }
  } catch (error) {
    logger.error("获取设备列表时出错:", error);
    ElMessage.error("网络错误，无法加载设备列表");
  } finally {
    loading.value = false;
  }
};

// 中文注释：持久化当前筛选与分页到会话存储
const persistToStorage = () => {
  try {
    const payload = {
      device_id: searchForm.device_id,
      deviceName: searchForm.deviceName,
      status: searchForm.status,
      license_status: searchForm.license_status,
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // 持久化失败时静默忽略，避免打断正常流程
  }
};

// 中文注释：从会话存储恢复筛选与分页
const restoreFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw || "{}") || {};
    if (typeof data.device_id === "string")
      searchForm.device_id = data.device_id;
    if (typeof data.deviceName === "string")
      searchForm.deviceName = data.deviceName;
    if (typeof data.status === "string") searchForm.status = data.status;
    if (typeof data.license_status === "string")
      searchForm.license_status = data.license_status;
    if (typeof data.page === "number" && data.page > 0)
      pagination.page = data.page;
    if (typeof data.pageSize === "number" && data.pageSize > 0)
      pagination.pageSize = data.pageSize;
    return true;
  } catch {
    return false;
  }
};

// 搜索处理
const handleSearch = () => {
  pagination.page = 1;
  persistToStorage();
  fetchDevices();
};

// 重置搜索
const resetSearch = () => {
  searchForm.device_id = "";
  searchForm.deviceName = "";
  searchForm.status = "";
  searchForm.license_status = ""; // 新增：重置License状态筛选
  searchForm.imsi = ""; // 新增：重置IMSI筛选
  pagination.page = 1;
  persistToStorage();
  fetchDevices();
};

// 分页大小变化
const handleSizeChange = (size: number) => {
  pagination.pageSize = size;
  persistToStorage();
  fetchDevices();
};

// 当前页码变化
const handleCurrentChange = (page: number) => {
  pagination.page = page;
  persistToStorage();
  fetchDevices();
};

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载设备列表");
  fetchDevices();
};

// 生命周期钩子
onMounted(() => {
  // 从 URL 查询参数获取 imsi
  const imsi = router.currentRoute.value.query.imsi as string;
  if (imsi) {
    searchForm.device_id = ""; // 清除其他筛选
    searchForm.deviceName = "";
    searchForm.status = "";
    searchForm.license_status = "";
    searchForm.imsi = imsi;
  }

  // 中文注释：从会话存储恢复筛选与分页（若有）
  if (!imsi) {
    restoreFromStorage();
  }
  fetchDevices();

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);

  const websocketManager = import("../services/websocketManager").then(
    (m) => m.default
  );
  websocketManager
    .then((wsManager) => {
      wsManager.autoConnect();

      unsubscribe = wsManager.onMessage((msg: any) => {
        if (typeof msg === "object" && msg !== null) {
          if (msg.type === "notify" && msg.action === "end_call") {
            const eventId = msg.data?.event_id;
            const deviceId = msg.data?.device_id;
            if (
              (currEventId.value && currEventId.value === eventId) ||
              (currDeviceId.value && currDeviceId.value === deviceId)
            ) {
              closeMessageBox();
            }
          } else if (msg.type === "response" && msg.action === "end_call") {
            const eventId = msg.data?.event_id;
            const deviceId = msg.data?.device_id;
            if (
              (currEventId.value && currEventId.value === eventId) ||
              (currDeviceId.value && currDeviceId.value === deviceId)
            ) {
              closeMessageBox();
            }
          }
        }
      });

      unsubscribeStatus = wsManager.onStatusChange(() => undefined);
    })
    .catch(() => undefined);
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  if (typeof unsubscribe === "function") {
    try {
      unsubscribe();
    } finally {
      unsubscribe = null;
    }
  }
  if (typeof unsubscribeStatus === "function") {
    try {
      unsubscribeStatus();
    } finally {
      unsubscribeStatus = null;
    }
  }
});

// 状态显示转换
const getStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    online: "success",
    offline: "warning",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    online: "在线",
    offline: "离线",
  };
  return textMap[status] || "未知";
};

// 网络类型判定与提示
const normalizeNetType = (row: Device) =>
  String(row?.network_type || "").toLowerCase();
const isWifi = (row: Device) => normalizeNetType(row) === "wifi";
const isCellular = (row: Device) => {
  const v = normalizeNetType(row);
  return v === "4g" || v === "lte" || v === "mobile" || v === "cellular";
};
const getNetworkTypeTooltip = (row: Device) =>
  isWifi(row) ? "WiFi" : isCellular(row) ? "4G" : "";

// License状态显示转换
const getLicenseStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    active: "success",
    inactive: "warning",
    expired: "danger",
  };
  return statusMap[status] || "info";
};

const getLicenseStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    active: "已激活",
    inactive: "未激活",
    expired: "已过期",
  };
  return textMap[status] || "未知";
};

// 打开新增设备弹窗
const addDevice = () => {
  isEditMode.value = false;
  dialogTitle.value = "注册新设备";
  dialogVisible.value = true;
};

// 打开编辑设备弹窗
const editDevice = (device: Device) => {
  isEditMode.value = true;
  dialogTitle.value = "编辑设备";
  // 使用 nextTick 确保表单在数据赋值前已存在
  nextTick(() => {
    Object.assign(deviceForm, device);
  });
  dialogVisible.value = true;
};

// 关闭弹窗并重置表单
const closeDialog = () => {
  dialogVisible.value = false;
  deviceFormRef.value?.resetFields();
  // 重置状态
  Object.assign(deviceForm, {
    id: null,
    device_id: "",
    device_name: "",
    device_model: "",
    hardware_version: "",
    firmware_version: "",
    status: "offline",
    license_status: "inactive",
  });
};

// 保存设备 (新增或更新)
const handleSave = async () => {
  if (!deviceFormRef.value) return;
  await deviceFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true;
      try {
        let response;
        if (isEditMode.value) {
          // 编辑模式 - PUT 请求
          const url = `${config.apiUrls.DEVICES}/${deviceForm.device_id}`;
          response = await apiClient.put(url, deviceForm);
        } else {
          // 新增模式 - POST 请求 (注册)
          // 根据文档，注册接口是 /api/v1/devices/register
          const url = `${config.apiUrls.DEVICES}/register`;
          response = await apiClient.post(url, deviceForm);
        }

        if (response.data.code === 0) {
          ElMessage.success(isEditMode.value ? "设备更新成功" : "设备注册成功");
          closeDialog();
          fetchDevices();
        } else {
          ElMessage.error(response.data.message || "操作失败");
        }
      } catch (error: unknown) {
        logger.error("保存设备时出错:", error);
        const errMsg =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || "网络错误，操作失败";
        ElMessage.error(errMsg);
      } finally {
        loading.value = false;
      }
    }
  });
};

// 查看设备详情 (跳转到详情页)
const viewDevice = (device: Device) => {
  router.push(`/device/${device.device_id}`);
};

// 查看通话明细 (跳转到通话明细页)
const viewCallRecords = (device: Device) => {
  router.push(`/call-records/${device.device_id}`);
};

// 自动获取设备名称列表（静默执行）
const fetchDeviceNames = async () => {
  try {
    // 准备请求参数，包含签名验证所需的参数
    const params = {
      pageNum: 1,
      pageSize: 100,
      timestamp: Math.floor(Date.now() / 1000).toString(),
    };

    logger.info("📋 自动获取设备名称列表", params);
    const response = await DataService.getDeviceNames(params);

    if (response.data?.code === 200) {
      const deviceNames = response.data.data?.deviceNames || [];
      logger.info(`成功获取 ${deviceNames.length} 个设备名称`);

      // 静默处理，不显示弹窗
      if (deviceNames.length > 0) {
        // 可以在这里处理设备名称数据，比如更新设备列表中的名称
        logger.info("设备名称数据:", deviceNames.slice(0, 3)); // 只记录前3个
      }
    } else {
      logger.warn("获取设备名称失败:", response.data?.message);
    }
  } catch (error) {
    logger.error("获取设备名称失败:", error);
    // 静默失败，不影响主流程
  }
};

// 使用通用通话弹窗逻辑
const {
  currDeviceId,
  currEventId,
  handleCallResponse,
  startWebRTC,
  leaveRoom,
  updateMessageBoxToAnswering,
  closeMessageBox,
  alertBroadcast,
} = useCallDialog();

// 发起呼叫
const initiateCall = async (device: Device) => {
  logger.debug("[initiateCall] device:", device);
  try {
    // 检查WebSocket连接状态
    const websocketManager = await import("../services/websocketManager").then(
      (m) => m.default
    );

    // 确保WebSocket已连接
    if (websocketManager.getStatus() !== "Connected") {
      logger.warn("WebSocket未连接，尝试自动连接...");
      websocketManager.autoConnect();

      // 等待连接建立
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("WebSocket连接超时"));
        }, 5000);

        const unsubscribe = websocketManager.onStatusChange((status) => {
          if (status === "Connected") {
            clearTimeout(timeout);
            unsubscribe();
            resolve(true);
          } else if (status === "Disconnected") {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error("WebSocket连接失败"));
          }
        });
      });
    }

    // 构建呼叫消息
    const callMessage = {
      type: "command",
      action: "start_call",
      messageId: `msg_${Date.now()}`,
      timestamp: new Date().toISOString(),
      data: {
        device_id: device.device_id,
        source: "frontend",
      },
    };
    logger.debug("[initiateCall] callMessage:", callMessage);

    // 发送呼叫消息
    websocketManager.send(callMessage);
    ElMessage.success(`正在呼叫设备 ${getDeviceDisplayName(device)}...`);

    // 使用Promise包装消息监听，确保只处理一次（避免使用 responsePromise 命名引起混淆）
    const waitForStartCallResponse = new Promise((resolve, reject) => {
      let isHandled = false;
      let timeoutId: number | undefined = undefined;

      const unsubscribe = websocketManager.onMessage((message) => {
        // 防止重复处理
        if (isHandled) {
          return;
        }

        logger.debug("[initiateCall] received message:", message);
        logger.info(
          `收到WebSocket消息: 类型=${message?.type}, 动作=${message?.action}`
        );

        if (message.type === "response" && message.action === "start_call") {
          isHandled = true;
          unsubscribe(); // 立即取消订阅

          // 清理超时定时器
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }

          if (message.code === 0) {
            ElMessage.success("呼叫成功，正在建立连接...");
            // 直接处理通话，不跳转页面 - Web端发起的呼叫
            handleCallResponse(message, device, true);
            resolve(true);
          } else {
            ElMessage.error(`呼叫失败: ${message.message || "未知错误"}`);
            reject(new Error(message.message || "呼叫失败"));
          }
        } else if (message.type === "notify" && message.action === "end_call") {
          // 处理通话结束通知
          logger.info("收到通话结束通知:", message.data);
          logger.info(
            `当前通话状态: currDeviceId=${currDeviceId.value}, currEventId=${currEventId.value}`
          );

          // 检查是否是当前设备的通话
          if (
            currDeviceId.value === message.data.device_id &&
            currEventId.value === message.data.event_id
          ) {
            logger.info("收到当前设备的通话结束通知，关闭弹窗");
            closeMessageBox();
            ElMessage.info(`设备已结束通话`);
          } else {
            logger.info("不是当前设备的通话，忽略此通知");
          }
        }
      });

      // 设置超时
      timeoutId = window.setTimeout(() => {
        if (!isHandled) {
          isHandled = true;
          unsubscribe();
          reject(new Error("呼叫超时，请检查设备状态"));
        }
      }, 10000); // 10秒超时
    });

    // 等待响应
    await waitForStartCallResponse;
  } catch (error) {
    logger.error("发起呼叫时出错:", error);
    const errorMessage = error instanceof Error ? error.message : "请稍后重试";
    ElMessage.error(`发起呼叫失败: ${errorMessage}`);
  }
};
</script>

<style lang="scss" scoped>
.net-icon {
  width: 16px;
  height: 16px;
  display: inline-block;
  vertical-align: middle;
}
.device-management {
  padding: 20px;
  background-color: #f5f7fa;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-section,
.main-content {
  width: 100%;
}

.search-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-card {
  box-shadow: none;
}

.search-form {
  padding: 10px 0;
}

.main-content {
  display: flex;
  gap: 16px;
}

.left-content {
  flex: 3;
  min-width: 0;
}

.right-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.signal-strength {
  display: flex;
  align-items: center;
  gap: 8px;
}

.signal-text {
  font-size: 12px;
  color: #666;
  min-width: 35px;
}

.action-buttons {
  display: flex !important;
  gap: 4px !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
}

/* 强制操作列单元格内容居中 */
.el-table .el-table__cell:last-child {
  text-align: center !important;
}

.action-buttons .el-button {
  margin: 0 !important;
}

.table-footer {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

/* 右侧统计面板样式 */
.stats-card,
.area-card,
.firmware-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 4px;
  &.online {
    background-color: #f0f9eb;
  }
  &.offline {
    background-color: #f4f4f5;
  }
  &.error {
    background-color: #fef0f0;
  }
  &.total {
    background-color: #ecf5ff;
  }

  .stat-icon {
    font-size: 28px;
  }
  .online .stat-icon {
    color: #67c23a;
  }
  .offline .stat-icon {
    color: #909399;
  }
  .error .stat-icon {
    color: #f56c6c;
  }
  .total .stat-icon {
    color: #409eff;
  }

  .stat-content {
    .stat-number {
      font-size: 22px;
      font-weight: bold;
    }
    .stat-label {
      font-size: 12px;
      color: #606266;
    }
  }
}

.area-list .area-item {
  margin-bottom: 12px;
  .area-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
    font-size: 14px;
  }
}

.firmware-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.firmware-item {
  padding: 8px 0;
}

.firmware-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.firmware-version {
  font-size: 14px;
  color: #333;
  font-family: "Courier New", monospace;
}

.firmware-count {
  font-size: 12px;
  color: #666;
}

.upgrade-info h4 {
  margin-bottom: 16px;
  color: #333;
}

.upgrade-info p {
  margin: 8px 0;
  color: #666;
}

/* 按钮图标间距调整 */
.el-button .el-icon {
  margin-right: 6px;
}

.el-button .el-icon:only-child {
  margin-right: 0;
}

/* 按钮组样式 */
.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
